import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Download, BookOpen, ArrowRight, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

// P04/P05 — Home dell'utente autenticato. Nessun blocco pacchetto/acquisto.
export default function Home() {
  const navigate = useNavigate();
  const [emptyPdfAvailable, setEmptyPdfAvailable] = useState(false);
  const [store, setStore] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null); // lookup_key in corso

  useEffect(() => {
    api
      .get("/assets/status")
      .then((r) => setEmptyPdfAvailable(!!r.data.empty_pdf_available))
      .catch(() => {});
    api
      .get("/store/offers")
      .then((r) => setStore(r.data))
      .catch(() => {});
  }, []);

  const eur = (n) =>
    "€" + Number(n).toFixed(2).replace(".", ",");

  // Avvio Checkout Stripe: il client invia SOLO il lookup_key; prezzo e quantità
  // sono decisi dal server. Si viene reindirizzati alla pagina di pagamento Stripe.
  const startCheckout = async (lookupKey) => {
    if (checkingOut) return;
    setCheckingOut(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile avviare il pagamento.");
      setCheckingOut(null);
    }
  };

  // PDF ufficiale dell'album VUOTO (gratuito). Distinto dal PDF completo in "Il Mio Album".
  const downloadEmptyPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/assets/album-empty`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // A download avviato, porta l'utente a "Il Mio Album".
    setTimeout(() => navigate("/album"), 500);
  };

  return (
    <div className="min-h-screen bg-[#fdf6ec]" data-testid="home-page">
      {/* Barra superiore: brand + icona Account */}
      <header className="flex items-center justify-between px-6 py-4 max-w-3xl mx-auto">
        <div className="flex items-center gap-2">
          <img src="/icon-192.png" alt="" className="w-9 h-9 rounded-xl" />
          <span className="font-extrabold text-[#2b2622] tracking-tight">
            AMOCANIAMOGATTI
          </span>
        </div>
        <button
          data-testid="account-icon-btn"
          onClick={() => navigate("/account")}
          aria-label="Account"
          className="w-10 h-10 rounded-full bg-white border border-[#f0e4d4] flex items-center justify-center text-[#7a6f64] hover:text-[#c85f45] transition-colors"
        >
          <User size={20} />
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-16 flex flex-col gap-8">
        {/* Copertina ufficiale */}
        <section
          data-testid="home-cover"
          className="flex flex-col items-center gap-4"
        >
          <img
            src="/cover-amocaniamogatti.png"
            alt="Copertina ufficiale AMOCANIAMOGATTI — album di figurine di cani e gatti"
            data-testid="home-cover-image"
            className="w-full max-w-md h-auto rounded-3xl border border-[#f0e4d4] shadow-[0_12px_40px_rgba(232,131,107,0.18)]"
          />
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-[#2b2622]">
              Il tuo album digitale
            </h1>
            <p className="text-sm text-[#7a6f64] max-w-md mx-auto mt-2">
              132 figurine di cani e gatti. Zero doppioni: ogni pacchetto fa avanzare la tua collezione.
            </p>
          </div>
        </section>

        {/* CTA dominante — SCARICA L'ALBUM GRATIS (PDF album vuoto) */}
        <section className="flex flex-col items-center gap-3">
          {emptyPdfAvailable ? (
            <Button
              data-testid="download-pdf-btn"
              onClick={downloadEmptyPdf}
              className="w-full max-w-md bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-full py-7 text-base font-bold transition-colors"
            >
              <Download size={20} className="mr-2" />
              SCARICA L’ALBUM GRATIS
            </Button>
          ) : (
            <Button
              data-testid="download-pdf-btn"
              disabled
              className="w-full max-w-md bg-[#e8836b] text-white rounded-full py-7 text-base font-bold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Download size={20} className="mr-2" />
              SCARICA L’ALBUM GRATIS
            </Button>
          )}
          <span data-testid="download-pdf-hint" className="text-xs text-[#9a8f83]">
            {emptyPdfAvailable
              ? "PDF ufficiale dell’album vuoto (nessun account richiesto)"
              : "PDF ufficiale dell’album vuoto · in arrivo (nessun account richiesto)"}
          </span>
        </section>

        {/* Offerte / prezzi dei pacchetti — soglie e prezzi dal server.
            Nessun pagamento reale collegato (fase di sola visualizzazione). */}
        {store && (store.offers.length > 0 || store.bundle_all) && (
          <section
            data-testid="home-offers"
            className="rounded-3xl border border-[#f0e4d4] bg-white p-6 flex flex-col gap-4"
          >
            <div className="flex items-center gap-2">
              <ShoppingCart size={20} className="text-[#c85f45]" />
              <h2 className="text-lg font-bold text-[#2b2622]">Acquista pacchetti</h2>
              <span
                data-testid="offers-remaining"
                className="ml-auto text-xs font-semibold text-[#7a6f64]"
              >
                {store.remaining_packs}/{store.paid_packs_total} pacchetti disponibili
              </span>
            </div>

            {store.bundle_all && (
              <button
                type="button"
                data-testid="offer-bundle-all"
                onClick={() => startCheckout(store.bundle_all.lookup_key)}
                disabled={!!checkingOut}
                className="w-full text-left rounded-2xl border-2 border-[#e8836b] bg-[#fdeee7] p-5 flex items-center justify-between gap-4 hover:bg-[#fce3d8] transition-colors disabled:opacity-70"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-[#c85f45]">
                    Offerta iniziale · tutti i {store.bundle_all.quantity} pacchetti
                  </span>
                  <span className="text-xs text-[#7a6f64]">
                    Disponibile solo prima del primo acquisto
                  </span>
                </div>
                <div className="flex flex-col items-end">
                  <span data-testid="offer-bundle-price" className="text-2xl font-extrabold text-[#2b2622]">
                    {eur(store.bundle_all.price_eur)}
                  </span>
                  <span className="text-xs text-[#9a8f83] line-through">
                    {eur(store.bundle_all.regular_eur)}
                  </span>
                </div>
              </button>
            )}

            <div className="grid sm:grid-cols-3 gap-3">
              {store.offers.map((o) => (
                <button
                  type="button"
                  key={o.quantity}
                  data-testid={`offer-${o.quantity}`}
                  onClick={() => startCheckout(o.lookup_key)}
                  disabled={!!checkingOut}
                  className="rounded-2xl border border-[#f0e4d4] bg-[#fdf6ec] p-4 flex flex-col items-center gap-1 hover:border-[#e8836b] hover:bg-white transition-colors disabled:opacity-70"
                >
                  <span className="text-sm font-semibold text-[#7a6f64]">
                    {o.quantity} {o.quantity === 1 ? "pacchetto" : "pacchetti"}
                  </span>
                  <span data-testid={`offer-${o.quantity}-price`} className="text-xl font-extrabold text-[#2b2622]">
                    {eur(o.price_eur)}
                  </span>
                </button>
              ))}
            </div>

           
          </section>
        )}

        {/* Distinzione chiara App vs PDF */}
        <section
          data-testid="home-distinction"
          className="grid sm:grid-cols-2 gap-4"
        >
          <div className="rounded-2xl border border-[#f0e4d4] bg-white p-5">
            <h3 className="font-bold text-[#2b2622] mb-1">App · Il Mio Album</h3>
            <p className="text-sm text-[#7a6f64]">
              La tua collezione digitale personale, sincronizzata sul tuo account su ogni dispositivo.
            </p>
          </div>
          <div className="rounded-2xl border border-[#f0e4d4] bg-white p-5">
            <h3 className="font-bold text-[#2b2622] mb-1">PDF · Album gratis</h3>
            <p className="text-sm text-[#7a6f64]">
              Il PDF ufficiale dell’album vuoto da scaricare liberamente, senza registrazione.
            </p>
          </div>
        </section>

        {/* Ingresso a IL MIO ALBUM */}
        <button
          data-testid="open-album-btn"
          onClick={() => navigate("/album")}
          className="w-full rounded-3xl bg-[#2b2622] text-white p-6 flex items-center justify-between hover:bg-black transition-colors"
        >
          <span className="flex items-center gap-3">
            <BookOpen size={24} />
            <span className="text-lg font-bold">IL MIO ALBUM</span>
          </span>
          <ArrowRight size={22} />
        </button>
      </main>
    </div>
  );
}
